import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { ContractorPerformanceEvent, ContractorRegistry } from '../../lib/types';
import { useApiQuery, useCursorList } from '../../lib/useApi';
import { BackLink, EmptyState, ErrorNote, Field, LoadMoreButton, Modal, PageHeader, Spinner, StatCard, StatusBadge } from '../../ui/components';

const GRADES = ['GRADE_A', 'GRADE_B', 'GRADE_C', 'GRADE_D'] as const;
const EVENT_TYPES = ['MILESTONE_MET', 'MILESTONE_MISSED', 'QUALITY_ISSUE', 'SAFETY_INCIDENT', 'BREACH', 'COMMENDATION'] as const;

export function Contractors() {
  const { persona } = useAuth();
  const isStaff = (persona?.roles ?? []).some((r) => ['WORKS_OFFICER', 'ROADS_ENGINEER'].includes(r));
  const navigate = useNavigate();
  const [grade, setGrade] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [showNew, setShowNew] = useState(false);

  const basePath = useMemo(() => {
    const params = new URLSearchParams();
    params.set('limit', '25');
    if (grade) params.set('grade', grade);
    if (status) params.set('status', status);
    if (q.trim()) params.set('q', q.trim());
    return `/api/v1/m20/contractors?${params.toString()}`;
  }, [grade, status, q]);
  const list = useCursorList<ContractorRegistry>(basePath);

  return (
    <div>
      <PageHeader
        title="Contractor Registry"
        subtitle="Public grade lookup + performance-event-driven 0-100 score."
        actions={isStaff ? <button className="btn-primary" onClick={() => setShowNew(true)}>+ Register contractor</button> : undefined}
      />

      <div className="card mb-4 flex flex-wrap items-end gap-3 p-4">
        <Field label="Grade" className="w-40">
          <select className="field-input" value={grade} onChange={(e) => setGrade(e.target.value)}>
            <option value="">All grades</option>
            {GRADES.map((g) => <option key={g} value={g}>{g.replaceAll('_', ' ')}</option>)}
          </select>
        </Field>
        <Field label="Status" className="w-40">
          <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="SUSPENDED">Suspended</option>
            <option value="BLACKLISTED">Blacklisted</option>
          </select>
        </Field>
        <Field label="Search" className="min-w-[200px] flex-1">
          <input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} />
        </Field>
      </div>

      {list.error ? <div className="mb-4"><ErrorNote message={list.error} /></div> : null}

      <div className="card overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse">
          <thead>
            <tr>
              <th className="table-head">Contractor</th>
              <th className="table-head">Grade</th>
              <th className="table-head">Score</th>
              <th className="table-head">Status</th>
              <th className="table-head">Specialties</th>
            </tr>
          </thead>
          <tbody>
            {list.items.map((c) => (
              <tr key={c.id} className="table-row-link" onClick={() => navigate(`/infrastructure/contractors/${c.id}`)}>
                <td className="table-cell font-semibold text-slate-800">{c.contractorName}</td>
                <td className="table-cell text-xs">{c.grade.replaceAll('_', ' ')}</td>
                <td className="table-cell tabular-nums">{c.performanceScore.toFixed(0)}</td>
                <td className="table-cell"><StatusBadge status={c.status} /></td>
                <td className="table-cell text-xs text-slate-500">{c.specialties.join(', ') || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.loading ? <Spinner label="Loading contractors…" /> : null}
        {!list.loading && list.items.length === 0 ? <EmptyState title="No contractors registered" /> : null}
      </div>
      <LoadMoreButton nextCursor={list.nextCursor} loading={list.loading} onClick={list.loadMore} />

      {showNew ? (
        <NewContractorModal onClose={() => setShowNew(false)} onCreated={(c) => { setShowNew(false); navigate(`/infrastructure/contractors/${c.id}`); }} />
      ) : null}
    </div>
  );
}

function NewContractorModal({ onClose, onCreated }: { onClose: () => void; onCreated: (c: ContractorRegistry) => void }) {
  const [contractorName, setContractorName] = useState('');
  const [registrationNo, setRegistrationNo] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [specialties, setSpecialties] = useState('');
  const [grade, setGrade] = useState<(typeof GRADES)[number]>('GRADE_C');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<ContractorRegistry>('/api/v1/m20/contractors', {
        contractorName,
        registrationNo: registrationNo || undefined,
        contactEmail: contactEmail || undefined,
        contactPhone: contactPhone || undefined,
        specialties: specialties.split(',').map((s) => s.trim()).filter(Boolean),
        grade,
      });
      onCreated(created);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not register contractor');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Register contractor" onClose={onClose} wide>
      {error ? <div className="mb-3"><ErrorNote message={error} /></div> : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Contractor name" className="sm:col-span-2">
          <input className="field-input" value={contractorName} onChange={(e) => setContractorName(e.target.value)} />
        </Field>
        <Field label="Registration no.">
          <input className="field-input" value={registrationNo} onChange={(e) => setRegistrationNo(e.target.value)} />
        </Field>
        <Field label="Grade">
          <select className="field-input" value={grade} onChange={(e) => setGrade(e.target.value as typeof grade)}>
            {GRADES.map((g) => <option key={g} value={g}>{g.replaceAll('_', ' ')}</option>)}
          </select>
        </Field>
        <Field label="Contact email">
          <input className="field-input" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
        </Field>
        <Field label="Contact phone">
          <input className="field-input" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
        </Field>
        <Field label="Specialties (comma-separated)" className="sm:col-span-2">
          <input className="field-input" value={specialties} onChange={(e) => setSpecialties(e.target.value)} placeholder="ROADS, DRAINAGE" />
        </Field>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={busy || !contractorName} onClick={submit}>
          {busy ? 'Registering…' : 'Register'}
        </button>
      </div>
    </Modal>
  );
}

export function ContractorDetail() {
  const { id = '' } = useParams();
  const { persona } = useAuth();
  const isStaff = (persona?.roles ?? []).some((r) => ['WORKS_OFFICER', 'ROADS_ENGINEER'].includes(r));
  const contractorQ = useApiQuery<ContractorRegistry>(`/api/v1/m20/contractors/${id}`);
  const eventsQ = useApiQuery<{ items: ContractorPerformanceEvent[] }>(`/api/v1/m20/contractors/${id}/performance-events`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [eventType, setEventType] = useState<(typeof EVENT_TYPES)[number]>('MILESTONE_MET');
  const [eventNotes, setEventNotes] = useState('');

  const act = async (fn: () => Promise<unknown>, message?: string) => {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      await fn();
      contractorQ.reload();
      eventsQ.reload();
      if (message) setNote(message);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  if (contractorQ.loading) return <Spinner label="Loading contractor…" />;
  if (contractorQ.error || !contractorQ.data) return <ErrorNote message={contractorQ.error ?? 'Contractor not found'} />;
  const c = contractorQ.data;

  return (
    <div>
      <BackLink to="/infrastructure/contractors" />
      <PageHeader title={c.contractorName} subtitle={c.registrationNo ?? undefined} actions={<StatusBadge status={c.status} />} />
      {error ? <div className="mb-4"><ErrorNote message={error} /></div> : null}
      {note ? <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{note}</div> : null}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <StatCard label="Performance score" value={c.performanceScore.toFixed(0)} hint={`Grade ${c.grade.replaceAll('_', ' ')}`} tone={c.performanceScore >= 70 ? 'good' : c.performanceScore >= 40 ? 'warn' : 'bad'} />

          <div className="card p-5">
            <h2 className="mb-3 text-sm font-bold text-slate-700">Performance events</h2>
            <div className="mb-3 space-y-2">
              {eventsQ.data?.items.map((e) => (
                <div key={e.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm">
                  <div>
                    <div className="font-medium text-slate-800">{e.type.replaceAll('_', ' ')}</div>
                    {e.notes ? <div className="text-xs text-slate-500">{e.notes}</div> : null}
                  </div>
                  <span className={`font-bold tabular-nums ${e.scoreDelta >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{e.scoreDelta > 0 ? '+' : ''}{e.scoreDelta}</span>
                </div>
              ))}
              {eventsQ.data?.items.length === 0 ? <EmptyState title="No performance events recorded" /> : null}
            </div>
            {isStaff ? (
              <div className="flex flex-wrap items-end gap-2 border-t border-slate-100 pt-3">
                <Field label="Event type" className="w-48">
                  <select className="field-input" value={eventType} onChange={(e) => setEventType(e.target.value as typeof eventType)}>
                    {EVENT_TYPES.map((t) => <option key={t} value={t}>{t.replaceAll('_', ' ')}</option>)}
                  </select>
                </Field>
                <Field label="Notes" className="min-w-[200px] flex-1">
                  <input className="field-input" value={eventNotes} onChange={(e) => setEventNotes(e.target.value)} />
                </Field>
                <button
                  className="btn-secondary"
                  disabled={busy}
                  onClick={() => act(() => api.post(`/api/v1/m20/contractors/${id}/performance-events`, { type: eventType, notes: eventNotes || undefined }), 'Performance event recorded.')}
                >
                  Record event
                </button>
              </div>
            ) : null}
          </div>
        </div>

        <div className="card p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-700">Registry actions</h2>
          {!isStaff ? <p className="text-xs text-slate-400">Requires WORKS_OFFICER or ROADS_ENGINEER.</p> : null}
          {isStaff ? (
            <div className="space-y-2">
              <Field label="Reason (for suspend/blacklist)">
                <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
              </Field>
              {c.status === 'ACTIVE' ? (
                <button className="btn-secondary w-full" disabled={busy || !reason} onClick={() => act(() => api.post(`/api/v1/m20/contractors/${id}/suspend`, { reason }), 'Contractor suspended.')}>
                  Suspend
                </button>
              ) : null}
              {c.status !== 'BLACKLISTED' ? (
                <button className="btn-danger w-full" disabled={busy || !reason} onClick={() => act(() => api.post(`/api/v1/m20/contractors/${id}/blacklist`, { reason }), 'Contractor blacklisted.')}>
                  Blacklist
                </button>
              ) : null}
              {c.status !== 'ACTIVE' ? (
                <button className="btn-success w-full" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m20/contractors/${id}/reinstate`), 'Contractor reinstated.')}>
                  Reinstate to active
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
